/**
 * Editor for a {type:'tag'} condition — pick a static tag, or a performance
 * tag from the List Tag Rules catalog (both colored dots). Free text only
 * remains as the fallback when the rules can't be read (no permission).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';

import { Button, Input } from '@/components/ui';
import { useListTagRules } from '@/hooks/smswiser/lists';
import type { StaticTag } from '@/hooks/smswiser/static-tags/useStaticTags';

interface Props {
  mode: 'static' | 'perf';
  staticTags: StaticTag[];
  initial?: string;
  onApply: (tag: string) => void;
}

interface TagOption {
  key: string;
  name: string;
  color: string | null;
}

const dedupeByName = (options: TagOption[]): TagOption[] => {
  const seen = new Set<string>();
  return options.filter((o) => {
    const k = o.name.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const FreeTextFallback: FC<{ initial?: string; onApply: (t: string) => void }> = ({
  initial,
  onApply,
}) => {
  const [text, setText] = useState(initial ?? '');
  return (
    <div className="flex w-64 flex-col gap-2 p-2">
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Performance tag name, e.g. High FTD"
        autoFocus
      />
      <Button size="sm" disabled={!text.trim()} onClick={() => onApply(text.trim())}>
        Apply
      </Button>
    </div>
  );
};

export const TagConditionEditor: FC<Props> = ({
  mode,
  staticTags,
  initial,
  onApply,
}) => {
  const rules = useListTagRules();
  const [filter, setFilter] = useState('');

  if (mode === 'perf' && rules.isError)
    return <FreeTextFallback initial={initial} onApply={onApply} />;

  const options: TagOption[] =
    mode === 'static'
      ? staticTags.map((t) => ({ key: t.id, name: t.name, color: t.color }))
      : dedupeByName(
          (rules.data ?? []).map((r) => ({ key: r.id, name: r.name, color: r.color })),
        );
  const shown = options.filter((o) =>
    o.name.toLowerCase().includes(filter.trim().toLowerCase()),
  );
  const empty =
    mode === 'static'
      ? 'No static tags yet — create one under Tag Rules → Static.'
      : mode === 'perf' && rules.isLoading
        ? 'Loading tag rules…'
        : 'No list tag rules yet — create them on the Tag Rules page.';

  return (
    <div className="flex w-64 flex-col gap-1 p-1">
      <Input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Search tags…"
        autoFocus
      />
      <div className="flex max-h-56 flex-col gap-1 overflow-y-auto">
        {shown.length === 0 ? (
          <p className="p-2 text-xs text-muted-foreground">{empty}</p>
        ) : (
          shown.map((tag) => (
            <button
              key={tag.key}
              type="button"
              onClick={() => onApply(tag.name)}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
            >
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: tag.color ?? 'hsl(var(--muted-foreground))' }}
              />
              <span className="truncate">{tag.name}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
};
